import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd } from "react-icons/md";
import Helpers from "../../components/Controls/helpers";
import UserData from "../Controls/UserData";
import ConfirmDlg from "../Controls/ConfirmDlg";
import AutoComplete from "../Controls/AutoComplete";
import SkillEditModal from "../Modals/SkillEditModal";

function normalizeSkill(raw) {
  if (!raw || typeof raw === "string") return null;
  return {
    ID: raw.ID ?? raw.id ?? 0,
    Name: raw.Name ?? raw.name ?? "",
    ShortName: raw.ShortName ?? raw.shortName ?? raw.Name ?? raw.name ?? "",
  };
}

export default function SkillSearch(props) {
  const navigate = useNavigate();
  const [dlgOpen, setDlgOpen] = useState(false);
  const [userData] = useState(UserData.UserData() || {});
  const [skillSearchResults, updateSearchResults] = useState([]);
  const [searchData, setSearchData] = useState({ term: "", results: [] });
  const [pendingSkill, setPendingSkill] = useState(null);
  const [createSkillName, setCreateSkillName] = useState(null);
  const [inputValue, setInputValue] = useState(
    props.object && props.fieldDisplay ? props.object[props.fieldDisplay] || "" : ""
  );

  const allowCreate = props.allowCreate !== false && !!(props.onSelect || props.onChange);

  const trimmedInput = (inputValue || "").trim();
  const hasExactMatch = useMemo(() => {
    if (!trimmedInput) return false;
    const needle = trimmedInput.toLowerCase();
    const pool = [
      ...(searchData.results || []),
      ...skillSearchResults.filter((s) => (s.ID ?? s.id) > 0),
    ];
    return pool.some((s) => (s.Name || s.name || "").toLowerCase() === needle);
  }, [trimmedInput, searchData.results, skillSearchResults]);

  const showCreateButton = allowCreate && trimmedInput.length >= 2 && !hasExactMatch;

  function emitSelection(skill) {
    if (props.onSelect) {
      props.onSelect(skill);
      return;
    }
    if (props.onChange) {
      props.onChange(skill, props.field || "ID", "SkillSearch");
    }
  }

  function onSelect(name) {
    if (!name || typeof name !== "string") return;
    let value = name.trim();
    if (value.startsWith("Add Skill:")) value = value.replace(/^Add Skill:\s*/i, "").trim();

    const pool = searchData.results?.length ? searchData.results : skillSearchResults;
    const found =
      pool.find((r) => (r.Name || "").toLowerCase() === value.toLowerCase() && (r.ID || 0) > 0) ||
      null;

    if (props.onSelect || props.onChange) {
      setInputValue("");
      updateSearchResults([]);
      if (found) {
        emitSelection(found);
        return;
      }
      if (allowCreate && value.length >= 2) {
        setCreateSkillName(value);
        return;
      }
      emitSelection({ ID: 0, Name: value, ShortName: value });
      return;
    }

    if (!found || !found.ID) return;

    if (props.personEdit) {
      const known = (userData.skills || []).some((s) => (s.ID || s.id) === found.ID);
      if (!known) {
        setPendingSkill(found);
        setDlgOpen(true);
      } else {
        navigate("/personSkill/" + found.ID);
      }
    } else {
      navigate("/adminSkillEdit/" + found.ID);
    }
  }

  async function onDlgResponse(confirmed) {
    setDlgOpen(false);
    setInputValue("");
    if (!confirmed || !pendingSkill?.ID) return;
    await UserData.UpdateAsync("skills", pendingSkill, false);
    navigate("/personSkill/" + pendingSkill.ID);
  }

  async function onSkillSearchAsync(_e, newValue) {
    const term = newValue || "";
    setInputValue(term);

    if (!term || term.length < 2) {
      updateSearchResults([]);
      return;
    }

    if (
      searchData.results?.length &&
      searchData.term &&
      term.toLowerCase().startsWith(searchData.term.toLowerCase())
    ) {
      const filtered = searchData.results.filter((v) =>
        (v.Name || "").toLowerCase().includes(term.toLowerCase())
      );
      updateSearchResults(filtered);
      return;
    }

    const resp = await Helpers.SkillsAsync(term);
    const data = (Array.isArray(resp) ? resp : [])
      .map(normalizeSkill)
      .filter((s) => s && s.ID > 0);

    data.sort((a, b) => ((a.Name || "") < (b.Name || "") ? -1 : 1));
    setSearchData({ term, results: data });
    updateSearchResults(data);
  }

  function onSkillCreated(saved) {
    setCreateSkillName(null);
    if (!saved) return;
    const skill = normalizeSkill(saved) || {
      ID: saved.ID ?? saved.id ?? 0,
      Name: saved.Name ?? saved.name ?? "",
      ShortName: saved.ShortName ?? saved.shortName ?? saved.Name ?? saved.name ?? "",
    };
    setInputValue("");
    updateSearchResults([]);
    if (skill.ID > 0) emitSelection(skill);
  }

  return (
    <>
      <div className={`skill-search-row${showCreateButton ? " skill-search-row--with-add" : ""}`}>
        <AutoComplete
          className={"skillSearch " + (props.className || "")}
          label={props.label || props.name || "Skill search"}
          value={inputValue}
          options={skillSearchResults.map((e) => e.Name)}
          onInputChange={onSkillSearchAsync}
          onSelect={onSelect}
        />
        {showCreateButton ? (
          <button
            type="button"
            className="skill-search-add"
            title={`Add skill “${trimmedInput}”`}
            aria-label={`Add skill ${trimmedInput}`}
            data-testid="skill-search-add"
            onClick={() => setCreateSkillName(trimmedInput)}
          >
            <MdAdd size={22} aria-hidden />
          </button>
        ) : null}
      </div>
      <ConfirmDlg
        title="Add Skill?"
        content="Would you like to add this skill to your account?"
        open={dlgOpen}
        onResponse={onDlgResponse}
      />
      {createSkillName != null ? (
        <SkillEditModal
          skillId={0}
          initialName={createSkillName}
          onClose={onSkillCreated}
        />
      ) : null}
    </>
  );
}
